// Food_Log read path — Phase 4, numbers only.
//
// This module is the boundary between the Food view and SharePoint.
// getFoodLogForDate currently pulls the list and filters in the browser. When
// the list outgrows that, a server-side $filter replaces the body of that one
// function and no call site changes — which is the whole reason the filter
// lives here and not inline in the component.
//
// DATES. Graph returns Food_Log.Date as a full ISO instant even though the
// column is dateOnly, so every read goes through fromDateOnly (a string slice)
// and every Date built here is assembled from LOCAL components. new Date(row.Date)
// would parse the bare date as UTC midnight and render as the previous day west
// of Greenwich — the exact failure the write guard in figApi exists to prevent.
// toISOString() appears nowhere in this module for the same reason.
//
// TIMES. Food_Log.Time is text, not a dateTime — local wall clock as
// zero-padded 24-hour 'HH:MM'. It is read through fromTimeOnly for the same
// reason Date is read through fromDateOnly: the column is only as clean as the
// door a row came through, and a value that does not conform is treated as
// absent rather than shown as if it were a time. Nothing here parses a clock or
// converts a zone; the string is sorted as a string and rendered as stored.
//
// Log rows are self-contained snapshots: macros and minerals were denormalized
// at log time so a catalog correction cannot rewrite history. Nothing here
// imports foodData.js, and nothing here should.

import { getItems, fromDateOnly, fromTimeOnly, toDateOnly, FOOD_LISTS } from '../api/figApi';

// The totalled columns. Order is display order.
//
// Calcium joins on the same terms as every other entry here: totalNutrients
// reads it through isDisclosed, so an entry that never declared calcium
// contributes an undisclosed count rather than a zero. Nothing about the
// reducer changed to accommodate it — adding the string was the whole edit.
//
// IodineMcg and RetinolMcg live in DETAIL_NUTRIENTS below rather than here.
// This array is totalNutrients' DEFAULT key set, so appending to it would
// silently widen every call that passes no keys; callers that want the mcg
// pair say so by passing a key list that includes them.
export const NUTRIENTS = [
  'Calories', 'Protein', 'Fat', 'Carbs', 'Sodium', 'Potassium', 'Magnesium', 'Calcium',
];

// The two mcg columns, added after NUTRIENTS was fixed at the eight above.
// A separate array rather than an appended pair for the reason stated there:
// growing the default set changes every default-keys call at once, while a
// second array lets each caller compose the coverage it wants — its own
// keys, [...NUTRIENTS, ...DETAIL_NUTRIENTS], or the default. Same reducer,
// same disclosure rules; nothing in this module treats the two differently.
export const DETAIL_NUTRIENTS = ['IodineMcg', 'RetinolMcg'];

// Mirrors the MealSlot choice list. Nothing in the Today view reads it any
// more — the log is one chronological ledger, not four buckets — but it is kept
// in step with the column definition in figApi, which is likewise retained.
export const MEAL_SLOTS = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];

// The single distinction this whole view is built around: undefined and null
// mean "the source did not disclose it", 0 means "the source disclosed zero".
// Carbs: 0 and Magnesium: undefined must never collapse into the same total.
function isDisclosed(value) {
  return value !== undefined && value !== null;
}

export async function getFoodLogForDate(token, dateStr) {
  const rows = await getItems(token, FOOD_LISTS.LOG);
  return rows.filter(row => fromDateOnly(row.Date) === dateStr);
}

// Inclusive range, same single fetch and the same fromDateOnly read as the
// single-day path above — zero-padded 'YYYY-MM-DD' makes the range test a pair
// of string compares. This is the seam the header comment promises: when the
// list outgrows client-side filtering, a server-side $filter replaces these
// two bodies and no call site changes.
export async function getFoodLogForRange(token, startStr, endStr) {
  const rows = await getItems(token, FOOD_LISTS.LOG);
  return rows.filter(row => {
    const day = fromDateOnly(row.Date);
    return day !== null && day >= startStr && day <= endStr;
  });
}

// Per nutrient in `keys` (the front page's NUTRIENTS by default):
// { value, undisclosed }.
//   value       — the sum of every disclosed reading, or null when there were
//                 none. A real 0 sums as 0 and keeps value non-null.
//   undisclosed — how many rows withheld that nutrient.
// No `|| 0`, no Number(), no parseFloat(): rows arrive uncoerced from Graph and
// leave this function uncoerced. A coercion here would turn undefined into 0
// and silently understate the day.
export function totalNutrients(rows, keys = NUTRIENTS) {
  const totals = {};
  for (const key of keys) {
    let value = null;
    let undisclosed = 0;
    for (const row of rows) {
      const reading = row[key];
      if (!isDisclosed(reading)) {
        undisclosed += 1;
        continue;
      }
      value = value === null ? reading : value + reading;
    }
    totals[key] = { value, undisclosed };
  }
  return totals;
}

// Calories attributable to rows that did not come from the catalog. Same
// disclosure rule; null when no such row disclosed a calorie count.
export function estimatedCalories(rows) {
  let value = null;
  for (const row of rows) {
    if (row.EntrySource === 'Catalog') continue;
    const reading = row.Calories;
    if (!isDisclosed(reading)) continue;
    value = value === null ? reading : value + reading;
  }
  return value;
}

// The day in the order it was eaten. Ascending on Time, which is stored as
// zero-padded 24-hour 'HH:MM' precisely so that a lexical compare IS a
// chronological one — '08:05' < '12:00' < '18:15' as strings, with no clock
// parsed and no Date allocated. Padding is what makes this hold; it is enforced
// on the way in by toTimeOnly and re-checked here on the way out.
//
// Rows with no usable time sort LAST rather than first or nowhere. A row
// written through an unguarded door (MCP, a provisioning script, an older row
// predating the Time column) has a null or non-conforming value, and it must
// still appear: it is already counted in the totals above the list, so dropping
// it from the list would leave a day whose entries do not add up to its own
// header. Both partitions keep their original relative order, and Array.sort is
// stable, so equal times stay as they arrived. The input array is not mutated.
export function sortByTime(rows) {
  const timed = [];
  const untimed = [];
  for (const row of rows) {
    if (fromTimeOnly(row.Time) === null) untimed.push(row);
    else timed.push(row);
  }
  timed.sort((a, b) => (a.Time < b.Time ? -1 : a.Time > b.Time ? 1 : 0));
  return [...timed, ...untimed];
}

// 'YYYY-MM-DD' -> local Date. The Number() calls below coerce a date STRING the
// app itself produced, never a nutrient reading off a row.
export function localDateFromDateOnly(dateStr) {
  const [y, m, d] = dateStr.split('-');
  return new Date(Number(y), Number(m) - 1, Number(d));
}

// Step one day. Mutates a local Date through setDate, which rolls months and
// years and honours DST, then re-emits through the same guard every write uses.
export function shiftDay(dateStr, delta) {
  const d = localDateFromDateOnly(dateStr);
  d.setDate(d.getDate() + delta);
  return toDateOnly(d, 'Date', FOOD_LISTS.LOG);
}

export function formatDayHeader(dateStr) {
  return localDateFromDateOnly(dateStr).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}
